import React from "react";
import Header from "./header";
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  useTheme,
  useMediaQuery,
  Link,
} from "@mui/material";
import backgroundImg from "../assets/background.jpg";
import googleImg from "../assets/google.png";
import trustImg from "../assets/trust.png";
import yelpImg from "../assets/yelp.png";
// import AccidentAssessmentForm from "./assessment";
import AccidentAssessmentForm from "../components/forms/AccidentAssessmentForm";
import Footer from "./footer";
import { useParams } from "react-router-dom";
import { extractNumber } from "../utils/helper";
import TextBox from "../components/textbox";
import { useTranslation } from "../components/hooks/useTranslation";

const HomePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { funnelSource } = useParams<{ funnelSource?: string }>();

  const { t } = useTranslation();

  const sourceNumber = extractNumber(funnelSource);

  return (
    <>
      <Header />
      <Box
        sx={{
          backgroundImage: `url(${backgroundImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          padding: "2rem 0",
        }}
      >
        <Container maxWidth="lg">
          {/* Main Heading */}
          {sourceNumber === 2 && (
            <>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                align="center"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  textShadow: "2px 2px 4px rgba(0, 0, 0, 0.6)",
                  mt: 0,
                  fontSize: {
                    xs: "1.1rem",
                    sm: "1.1rem",
                    md: "1.7rem",
                  },
                }}
              >
                {t("homePageYT.firstTitle")}
                <br />
                <span
                  style={{
                    textDecoration: "underline",
                    textDecorationColor: "#ff6b35",
                    textDecorationThickness: "3px",
                  }}
                >
                  {t("homePageYT.secondTitle")}
                </span>{" "}
              </Typography>
              {/* Assessment Text */}
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "white",
                  textShadow: "1px 1px 3px rgba(0, 0, 0, 0.6)",
                  mb: isMobile ? 1 : 3,
                  mt: isMobile ? 1 : 3,
                  fontSize: {
                    xs: "1rem",
                    sm: "1rem",
                    md: "1.3rem",
                  },
                }}
              >
                {t("homePageYT.firstSubtitle")}{" "}
                <span style={{ color: "#ff6b35", fontWeight: "bold" }}>
                  {t("homePageYT.secondSubtitle")}
                </span>{" "}
                <span>{t("homePageYT.thirdSubtitle")}</span>
              </Typography>
            </>
          )}
          {sourceNumber !== 2 && (
            <>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                align="center"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  textShadow: "2px 2px 4px rgba(0, 0, 0, 0.6)",
                  mt: 0,
                  fontSize: {
                    xs: "1.3rem",
                    sm: "1.3rem",
                    md: "1.7rem",
                  },
                }}
              >
                {t("homePageFB.firstTitle")}{" "}
                <span
                  style={{
                    textDecoration: "underline",
                    textDecorationColor: "#ff6b35",
                    textDecorationThickness: "3px",
                  }}
                >
                  {t("homePageFB.secondTitle")}
                </span>{" "}
                {t("homePageFB.thirdTitle")}
              </Typography>

              <Typography
                variant={isMobile ? "h6" : "h4"}
                align="center"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  textShadow: "2px 2px 4px rgba(0, 0, 0, 0.6)",
                  mb: isMobile ? 1 : 2,
                  fontSize: {
                    xs: "1.3rem",
                    sm: "1.3rem",
                    md: "1.7rem",
                  },
                }}
              >
                {t("homePageFB.fourthTitle")}
              </Typography>

              {/* Assessment Text */}
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "white",
                  textShadow: "1px 1px 3px rgba(0, 0, 0, 0.6)",
                  mb: isMobile ? 1 : 3,
                  fontSize: {
                    xs: "0.9rem",
                    sm: "0.9rem",
                    md: "1.2rem",
                  },
                }}
              >
                {t("homePageFB.firstSubtitle")}{" "}
                <span style={{ color: "#ff6b35", fontWeight: "bold" }}>
                  {t("homePageFB.secondSubtitle")}
                </span>{" "}
                {t("homePageFB.thirdSubtitle")}{" "}
                <span style={{ color: "#ff6b35" }}>
                  {t("homePageFB.fourthSubtitle")}
                </span>
              </Typography>
            </>
          )}

          {sourceNumber === 3 && <TextBox />}

          {/* Assessment Form - now using the multi-step form component */}

          {sourceNumber !== 6 && (
            <Paper
              elevation={3}
              sx={{
                pt: 2,
                pb: 4,
                pl: 4,
                pr: 4,
                borderRadius: 2,
                mb: 4,
                width: "100%",
                maxWidth: "800px",
                mx: "auto",
              }}
            >
              <AccidentAssessmentForm sourceNumber={sourceNumber ?? 0} />
            </Paper>
          )}

          {sourceNumber === 6 && (
            <Paper
              elevation={3}
              sx={{
                pt: 2,
                pb: 4,
                pl: 4,
                pr: 4,
                borderRadius: 2,
                mb: 4,
                width: "100%",
                maxWidth: "800px",
                mx: "auto",
              }}
            >
              <AccidentAssessmentForm
                sourceNumber={6}
                skipSteps={["INJURY", "REPRESENTATION", "FAULT"]}
                defaultValues={{
                  physicalInjury: "YES",
                  treatedBy: "YES", // Set your desired default
                  hasAttorney: "NO", // Set your desired default
                  accidentFault: "NO", // Set your desired default
                }}
              />
            </Paper>
          )}

          {/* Free Consultation Text */}
          <Typography
            variant="subtitle1"
            align="center"
            sx={{
              color: "white",
              fontWeight: "medium",
              textShadow: "1px 1px 3px rgba(0, 0, 0, 0.6)",
              mb: 3,
            }}
          >
            {t("homepage.text")}
          </Typography>

          {/* Rating Text */}
          <Typography
            variant="h6"
            align="center"
            sx={{
              color: "white",
              fontWeight: "bold",
              textShadow: "1px 1px 3px rgba(0, 0, 0, 0.6)",
              mb: 3,
            }}
          >
            {t("homepage.text2")}
          </Typography>

          {/* Review Logos */}
          <Grid container spacing={2} justifyContent="center" sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <img
                src={yelpImg}
                alt="Yelp"
                style={{
                  height: "100px",
                  marginLeft: isMobile ? "18%" : "10%",
                  marginBottom: "8px",
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <img
                src={googleImg}
                alt="Google"
                style={{
                  height: "100px",
                  marginLeft: isMobile ? "18%" : "10%",
                  marginBottom: "8px",
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <img
                src={trustImg}
                alt="Trustpilot"
                style={{
                  height: "100px",
                  marginLeft: isMobile ? "18%" : "10%",
                  marginBottom: "8px",
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Container sx={{ mt: "2%" }} maxWidth="md">
        <Typography
          sx={{ mb: "10px", textAlign: "center" }}
          variant="subtitle1"
        >
          {t("homepage.tcpa1")}{" "}
          <Link
            href="https://www.caraccidentcounsel.com/terms-of-service/"
            sx={{ color: "#05b1eb" }}
          >
            {t("homepage.tcpa2")}
          </Link>{" "}
          {t("homepage.tcpa3")}{" "}
          <Link
            href="https://www.caraccidentcounsel.com/network-of-lawyers-and-advocates/"
            sx={{ color: "#05b1eb" }}
          >
            {t("homepage.tcpa4")}
          </Link>{" "}
          {t("homepage.tcpa5")}
        </Typography>
        <Typography
          sx={{ mb: "10px", textAlign: "center" }}
          variant="subtitle1"
        >
          {t("homepage.tcpa6")}
        </Typography>
        <Typography
          sx={{ mb: "10px", textAlign: "center" }}
          variant="subtitle1"
        >
          {t("homepage.tcpa7")}
        </Typography>
        <Typography
          sx={{ mb: "10px", textAlign: "center" }}
          variant="subtitle1"
        >
          {t("homepage.tcpa8")}{" "}
          <Link
            href="https://www.caraccidentcounsel.com/sponsors/"
            sx={{ color: "#05b1eb" }}
          >
            {t("homepage.tcpa9")}
          </Link>{" "}
          {t("homepage.tcpa10")}
        </Typography>
      </Container>
      <Footer />
    </>
  );
};

export default HomePage;
